/**
 * Provider settings form for a Communications channel: select the default provider,
 * toggle the channel, set retry/webhook/rate-limit, and manage write-only
 * credentials (values are never read back — only whether each key is configured).
 *
 * The Credentials section lists the keys each provider expects (e.g. 360dialog →
 * ApiKey / WabaId / BillingCurrency) as fillable rows with a Configured/Not-set badge,
 * plus a custom-key editor for anything not predefined.
 */

import { useEffect, useState } from 'react';
import {
  Stack,
  Group,
  Text,
  Title,
  Button,
  Select,
  Switch,
  NumberInput,
  TextInput,
  Textarea,
  PasswordInput,
  Badge,
  Loader,
  Alert,
  Divider,
} from '../../../../components/ui';
import { Card } from '../../../../components/ui/data-display';
import {
  useCommunicationSettings,
  CHANNEL_IDS,
} from './useCommunicationSettings';

type CredKeySpec = {
  key: string;
  label: string;
  secret: boolean;
  placeholder?: string;
  help?: string;
  /** Render a textarea: pasting a service-account JSON or a .p8 into a single-line input mangles it. */
  multiline?: boolean;
  /** Validate the value parses as JSON before allowing Save. */
  json?: boolean;
  /** Extra validation beyond "is it JSON"; return an error string, or null when the value is good. */
  validate?: (value: string) => string | null;
  /** Render a Select with these values instead of a free-text box. */
  options?: string[];
};

/** Expected credential keys per provider code. Drives the fillable credential rows. */
const PROVIDER_CREDENTIAL_KEYS: Record<string, CredKeySpec[]> = {
  DIALOG360: [
    { key: 'ApiKey', label: 'API Key', secret: true, placeholder: 'D360 channel API key', help: 'Sent as the D360-API-KEY header on every WhatsApp call.' },
    { key: 'WabaId', label: 'WhatsApp Business Account ID', secret: false, placeholder: 'e.g. 1234567890', help: 'Required for the Billing page (Meta conversation analytics).' },
    { key: 'BillingCurrency', label: 'Billing currency', secret: false, placeholder: 'USD', help: 'ISO code your WhatsApp account is billed in (display only).' },
  ],
  SES: [
    { key: 'AccessKey', label: 'AWS Access Key', secret: true },
    { key: 'SecretKey', label: 'AWS Secret Key', secret: true },
    { key: 'Region', label: 'Region', secret: false, placeholder: 'af-south-1' },
    { key: 'VerifiedEmail', label: 'Verified From address', secret: false, placeholder: 'noreply@actschurch.co.za', help: 'Must be an SES-verified sender (or domain). Used as the From for all emails.' },
    { key: 'DefaultFromName', label: 'Default From name', secret: false, placeholder: 'CCMS Notifications' },
    { key: 'ConfigurationSetName', label: 'Configuration set', secret: false, placeholder: 'ccms-email-events', help: 'SES configuration set that publishes delivery/bounce/open/click events to SNS (tracking).' },
  ],
  SNS: [
    { key: 'AccessKey', label: 'AWS Access Key', secret: true },
    { key: 'SecretKey', label: 'AWS Secret Key', secret: true },
    { key: 'Region', label: 'Region', secret: false, placeholder: 'af-south-1' },
  ],
  PINPOINT_SMS: [
    { key: 'AccessKey', label: 'AWS Access Key', secret: true },
    { key: 'SecretKey', label: 'AWS Secret Key', secret: true },
    { key: 'Region', label: 'Region', secret: false, placeholder: 'af-south-1' },
    { key: 'ApplicationId', label: 'Pinpoint Application ID', secret: false },
  ],
  TWILIO: [
    { key: 'AccountSid', label: 'Account SID', secret: false },
    { key: 'AuthToken', label: 'Auth Token', secret: true },
    { key: 'FromNumber', label: 'From number', secret: false, placeholder: '+1...' },
  ],
  GRAPEVINE: [
    { key: 'AffiliateCode', label: 'Affiliate Code', secret: false, help: 'GrapeVine account identifier (TELxxx-xxx).' },
    { key: 'AuthenticationCode', label: 'Authentication Code', secret: true, help: 'GrapeVine API password.' },
    { key: 'ApiUrl', label: 'API URL', secret: false, placeholder: 'https://bms27.vine.co.za/httpInputhandler/ApplinkUpload' },
  ],
  FCM: [
    { key: 'ServiceAccountJson', label: 'Service Account JSON', secret: true, multiline: true, json: true, validate: serviceAccountErrorFor, placeholder: '{\n  "type": "service_account",\n  "project_id": "…",\n  "private_key": "-----BEGIN PRIVATE KEY-----\\n…"\n}', help: 'Firebase Console → Project settings → Service accounts → Generate new private key. NOT google-services.json (that is the Android client config). One shared Firebase project covers Android + Web.' },
  ],
  APNS: [
    { key: 'ApnsKeyId', label: 'APNs Key ID', secret: false },
    { key: 'TeamId', label: 'Apple Team ID', secret: false },
    { key: 'ApnsAuthKey', label: 'APNs Auth Key (.p8)', secret: true, multiline: true, placeholder: '-----BEGIN PRIVATE KEY-----\n…\n-----END PRIVATE KEY-----', help: 'Full contents of the .p8 file, including the BEGIN/END lines and their line breaks. One key serves all iOS apps under the same Apple Team.' },
    { key: 'BundleId', label: 'Default Bundle ID (apns-topic)', secret: false, placeholder: 'co.za.actschurch.ccms.hub', help: 'Fallback apns-topic when an app isn’t in the map below.' },
    { key: 'ApnsBundleIds', label: 'Bundle IDs by appId (JSON)', secret: false, multiline: true, json: true, placeholder: '{\n  "ccms.hub": "co.za.actschurch.ccms.hub",\n  "ccms.task": "co.za.actschurch.ccms.task"\n}', help: 'Maps each constellation appId to its bundle id (apns-topic). Every iOS app needs an entry — a token sent with another app’s topic is rejected with DeviceTokenNotForTopic.' },
    { key: 'ApnsUseSandbox', label: 'Use APNs sandbox', secret: false, options: ['false', 'true'], help: 'false = production APNs (App Store and TestFlight builds). true = sandbox (Xcode development builds only).' },
  ],
  WEBPUSH: [
    { key: 'VapidSubject', label: 'VAPID Subject', secret: false, placeholder: 'mailto:admin@example.com' },
    { key: 'VapidPublicKey', label: 'VAPID Public Key', secret: false },
    { key: 'VapidPrivateKey', label: 'VAPID Private Key', secret: true },
  ],
};

/** Returns a human-readable error when the text isn't valid JSON, or null when it parses. */
function jsonErrorFor(value: string): string | null {
  try {
    const parsed = JSON.parse(value);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return 'Expected a JSON object.';
    }
    return null;
  } catch (e: any) {
    return `Not valid JSON: ${e?.message ?? 'parse error'}`;
  }
}

/**
 * The FCM server credential is the *service account* key (Firebase Console → Project settings →
 * Service accounts → Generate new private key) — NOT google-services.json / GoogleService-Info.plist,
 * which are client config files. Pasting the client file is an easy mistake and the API accepts it
 * silently; the failure only shows up later as "Unrecognized credential type" on every send.
 */
function serviceAccountErrorFor(value: string): string | null {
  const jsonError = jsonErrorFor(value);
  if (jsonError) return jsonError;

  const parsed = JSON.parse(value) as Record<string, unknown>;

  if ('project_info' in parsed || 'client' in parsed) {
    return 'This looks like google-services.json (the Android client config). Use the service account key instead: Firebase Console → Project settings → Service accounts → Generate new private key.';
  }

  if (parsed.type !== 'service_account') {
    return 'Missing "type": "service_account" — this is not a service account key.';
  }

  const missing = ['project_id', 'private_key', 'client_email'].filter((k) => !parsed[k]);
  if (missing.length > 0) {
    return `Service account key is missing: ${missing.join(', ')}.`;
  }

  return null;
}

export function ProviderSettingsForm({ channel }: { channel: 'email' | 'sms' | 'whatsapp' | 'push' }) {
  const channelId = CHANNEL_IDS[channel];
  const { loading, saving, error, settings, fetchSettings, updateSettings, setCredential, revealCredential } = useCommunicationSettings();

  // Push isn't a single-provider channel: FCM (Android/Web) and APNs (iOS) are used together, so
  // its credentials are configured per-provider rather than gated on one "default provider".
  const multiProvider = channel === 'push';

  const [providerId, setProviderId] = useState<string | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [maxRetry, setMaxRetry] = useState<number | string>('');
  const [backoff, setBackoff] = useState<number | string>('');
  const [rateLimit, setRateLimit] = useState<number | string>('');
  const [webhookUrl, setWebhookUrl] = useState('');

  // Per-key credential inputs, keyed by `${providerId}:${keyName}` so providers that share a key
  // name (e.g. AWS AccessKey) don't collide. Plus a custom-key editor (single-provider channels).
  const [credInputs, setCredInputs] = useState<Record<string, string>>({});
  const [credKey, setCredKey] = useState('');
  const [credValue, setCredValue] = useState('');
  const [credSecret, setCredSecret] = useState(true);
  // Keys saved this session (`${providerId}:${keyName}`) — flips the badge to "Configured"
  // immediately, even before the backend's per-provider status read is deployed.
  const [justSaved, setJustSaved] = useState<Set<string>>(new Set());
  // Revealed secret values (`${providerId}:${keyName}` → plaintext), fetched on demand.
  const [revealed, setRevealed] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchSettings(channelId);
  }, [channelId, fetchSettings]);

  useEffect(() => {
    if (!settings) return;
    setProviderId(settings.defaultProviderId != null ? String(settings.defaultProviderId) : null);
    setEnabled(settings.isEnabled);
    setMaxRetry(settings.maxRetryCount ?? '');
    setBackoff(settings.retryBackoffSeconds ?? '');
    setRateLimit(settings.rateLimitPerMinute ?? '');
    setWebhookUrl(settings.webhookUrl ?? '');
  }, [settings]);

  if (loading && !settings) {
    return (
      <Group justify="center" p="xl">
        <Loader size="lg" />
      </Group>
    );
  }

  if (error) {
    return <Alert color="red" title="Failed to load settings">{error}</Alert>;
  }

  const handleSave = async () => {
    await updateSettings(channelId, {
      defaultProviderId: providerId != null ? Number(providerId) : null,
      isEnabled: enabled,
      maxRetryCount: maxRetry === '' ? null : Number(maxRetry),
      retryBackoffSeconds: backoff === '' ? null : Number(backoff),
      rateLimitPerMinute: rateLimit === '' ? null : Number(rateLimit),
      webhookUrl: webhookUrl || null,
    });
  };

  const inputKey = (pid: number, key: string) => `${pid}:${key}`;

  const saveKey = async (pid: number, key: string, secret: boolean) => {
    const value = (credInputs[inputKey(pid, key)] ?? '').trim();
    if (!pid || !value) return;
    const ok = await setCredential(channelId, pid, key, value, secret);
    if (ok) {
      setCredInputs((p) => ({ ...p, [inputKey(pid, key)]: '' }));
      setJustSaved((prev) => new Set(prev).add(inputKey(pid, key)));
    }
  };

  const doReveal = async (pid: number, key: string) => {
    const ik = inputKey(pid, key);
    const value = await revealCredential(channelId, pid, key);
    if (value != null) setRevealed((prev) => ({ ...prev, [ik]: value }));
  };

  const hideRevealed = (pid: number, key: string) =>
    setRevealed((prev) => {
      const next = { ...prev };
      delete next[inputKey(pid, key)];
      return next;
    });

  const handleSaveCredential = async () => {
    if (!providerId || !credKey.trim()) return;
    const ok = await setCredential(channelId, Number(providerId), credKey.trim(), credValue, credSecret);
    if (ok) {
      setCredKey('');
      setCredValue('');
    }
  };

  const providers = settings?.providers ?? [];
  const providerData = providers.map((p) => ({ value: String(p.providerId), label: p.name }));
  const selectedCode = providers.find((p) => String(p.providerId) === providerId)?.code ?? '';
  const credFor = (pid: number, key: string) =>
    settings?.credentials?.find((c) => c.providerId === pid && c.keyName.toLowerCase() === key.toLowerCase());

  // Renders the credential rows for one provider: its expected keys + any other configured keys.
  const renderProviderCredentials = (pid: number, code: string) => {
    const expectedKeys = PROVIDER_CREDENTIAL_KEYS[code] ?? [];
    const expectedSet = new Set(expectedKeys.map((k) => k.key.toLowerCase()));
    const extraCreds = (settings?.credentials ?? []).filter(
      (c) => c.providerId === pid && !expectedSet.has(c.keyName.toLowerCase()),
    );

    return (
      <Stack gap="md">
        {expectedKeys.length > 0 ? (
          expectedKeys.map((k) => {
            const ik = inputKey(pid, k.key);
            const isSet = !!credFor(pid, k.key)?.isSet || justSaved.has(ik);
            const raw = credInputs[ik] ?? '';
            // Block Save on a malformed value: a bad service account or bundle-id map is accepted
            // silently by the API and only surfaces later as a failed send.
            const jsonError = raw.trim()
              ? (k.validate ? k.validate(raw) : k.json ? jsonErrorFor(raw) : null)
              : null;
            return (
              <div key={ik}>
                <Group gap="xs" mb={4}>
                  <Text size="sm" fw={500}>{k.label}</Text>
                  <Badge size="xs" variant="light" color={isSet ? 'green' : 'gray'}>
                    {isSet ? 'Configured' : 'Not set'}
                  </Badge>
                </Group>
                {k.help && <Text size="xs" c="dimmed" mb={4}>{k.help}</Text>}
                <Group align="flex-end" gap="xs" wrap="nowrap">
                  {k.options ? (
                    <Select
                      style={{ flex: 1 }}
                      data={k.options}
                      placeholder={k.placeholder || 'Select'}
                      value={credInputs[ik] ?? null}
                      onChange={(v) => setCredInputs((p) => ({ ...p, [ik]: v ?? '' }))}
                    />
                  ) : k.multiline ? (
                    <Textarea
                      style={{ flex: 1 }}
                      autosize
                      minRows={4}
                      maxRows={12}
                      spellCheck={false}
                      styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
                      placeholder={isSet ? '•••••• (set — paste to replace)' : (k.placeholder || 'Value')}
                      value={raw}
                      error={jsonError || undefined}
                      onChange={(e) => setCredInputs((p) => ({ ...p, [ik]: e.currentTarget.value }))}
                    />
                  ) : k.secret ? (
                    <PasswordInput
                      style={{ flex: 1 }}
                      placeholder={isSet ? '•••••• (set — enter to replace)' : (k.placeholder || 'Value')}
                      value={raw}
                      onChange={(e) => setCredInputs((p) => ({ ...p, [ik]: e.currentTarget.value }))}
                    />
                  ) : (
                    <TextInput
                      style={{ flex: 1 }}
                      placeholder={k.placeholder || 'Value'}
                      value={raw}
                      onChange={(e) => setCredInputs((p) => ({ ...p, [ik]: e.currentTarget.value }))}
                    />
                  )}
                  <Button variant="light" loading={saving} disabled={!raw.trim() || !!jsonError} onClick={() => saveKey(pid, k.key, k.secret)}>
                    Save
                  </Button>
                </Group>

                {/* Current saved value — non-secret shown directly; secret behind a Reveal action. */}
                {isSet && !k.secret && credFor(pid, k.key)?.value && (
                  <Text size="xs" c="dimmed" mt={4} style={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
                    Current: {credFor(pid, k.key)?.value}
                  </Text>
                )}
                {isSet && k.secret && (
                  revealed[ik] !== undefined ? (
                    <Group gap="xs" mt={4} wrap="nowrap" align="flex-start">
                      <Text size="xs" c="dimmed" style={{ wordBreak: 'break-all', fontFamily: 'monospace', flex: 1 }}>
                        Current: {revealed[ik]}
                      </Text>
                      <Button variant="subtle" size="xs" onClick={() => hideRevealed(pid, k.key)}>Hide</Button>
                    </Group>
                  ) : (
                    <Button variant="subtle" size="xs" mt={4} onClick={() => doReveal(pid, k.key)}>
                      Reveal current value
                    </Button>
                  )
                )}
              </div>
            );
          })
        ) : (
          <Text size="sm" c="dimmed">No predefined keys for this provider — use the custom key field below.</Text>
        )}

        {extraCreds.length > 0 && (
          <>
            <Divider label="Other configured keys" labelPosition="left" />
            {extraCreds.map((c) => (
              <Group key={`${pid}:${c.keyName}`} justify="space-between">
                <Text size="sm">{c.keyName}</Text>
                <Badge size="sm" variant="light" color={c.isSet ? 'green' : 'gray'}>
                  {c.isSet ? 'Configured' : 'Not set'}
                </Badge>
              </Group>
            ))}
          </>
        )}
      </Stack>
    );
  };

  return (
    <Stack gap="lg">
      {/* Provider + channel config */}
      <Card withBorder padding="lg">
        <Stack gap="md">
          <Title order={5}>Provider</Title>
          {multiProvider ? (
            <Alert color="blue">
              Push uses <b>all</b> configured providers together — <b>FCM</b> for Android &amp; web and{' '}
              <b>APNs</b> for iOS. There's no single "default"; configure each provider's credentials below.
            </Alert>
          ) : (
            <Select
              label="Default provider"
              placeholder="Select a provider"
              data={providerData}
              value={providerId}
              onChange={setProviderId}
              description="The provider used to send on this channel."
            />
          )}
          <Switch
            label="Channel enabled"
            checked={enabled}
            onChange={(e) => setEnabled(e.currentTarget.checked)}
          />
          <Group grow>
            <NumberInput label="Max retries" value={maxRetry} onChange={setMaxRetry} min={0} max={10} />
            <NumberInput label="Retry backoff (s)" value={backoff} onChange={setBackoff} min={0} />
            <NumberInput label="Rate limit (per min)" value={rateLimit} onChange={setRateLimit} min={0} />
          </Group>
          <TextInput
            label="Webhook URL"
            placeholder="https://…"
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.currentTarget.value)}
            description="Inbound / status callback URL (where applicable)."
          />
          <Group justify="flex-end">
            <Button onClick={handleSave} loading={saving}>Save settings</Button>
          </Group>
        </Stack>
      </Card>

      {/* Credentials */}
      <Card withBorder padding="lg">
        <Stack gap="md">
          <Title order={5}>Credentials</Title>
          <Text size="sm" c="dimmed">
            Stored encrypted; values are never displayed.{' '}
            {channel === 'whatsapp' || channel === 'push'
              ? 'Saved values are applied to sending within ~1 minute (DB overrides appsettings).'
              : 'Stored securely — runtime wiring for this channel is pending (sending still uses appsettings).'}
          </Text>

          {multiProvider ? (
            // Push: one section per active provider (FCM, APNs, and any legacy WebPush).
            providers.length === 0 ? (
              <Text size="xs" c="orange">No push providers are configured.</Text>
            ) : (
              <Stack gap="lg">
                {providers.map((p) => (
                  <div key={p.providerId}>
                    <Group gap="xs" mb="xs">
                      <Title order={6}>{p.name}</Title>
                      {p.description && <Text size="xs" c="dimmed">{p.description}</Text>}
                    </Group>
                    {renderProviderCredentials(p.providerId, p.code)}
                  </div>
                ))}
              </Stack>
            )
          ) : !providerId ? (
            <Text size="xs" c="orange">Select and save a default provider before adding credentials.</Text>
          ) : (
            <Stack gap="md">
              {renderProviderCredentials(Number(providerId), selectedCode)}

              <Divider label="Add custom key" labelPosition="left" />
              <Group align="flex-end" grow>
                <TextInput label="Key name" placeholder="ApiKey" value={credKey} onChange={(e) => setCredKey(e.currentTarget.value)} />
                <PasswordInput label="Value" placeholder="••••••" value={credValue} onChange={(e) => setCredValue(e.currentTarget.value)} />
              </Group>
              <Group justify="space-between">
                <Switch label="Secret (encrypt at rest)" checked={credSecret} onChange={(e) => setCredSecret(e.currentTarget.checked)} />
                <Button variant="light" onClick={handleSaveCredential} loading={saving} disabled={!credKey.trim()}>
                  Save credential
                </Button>
              </Group>
            </Stack>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
